import React from "react";
import { Box, Button, Heading } from "@vinomofo/components";
import styled from "styled-components";
import { COLORS, FONT_FAMILIES } from "../../styles";
import { useLocation } from "@reach/router";
import { InlineCountdownTimer } from "../inline-countdown-timer/InlineCountdownTimer";
import { HubspotForm } from "../hubspot-form/HubspotForm";
import { isWhiskymofoSignupPage } from "../../utils/is-page";
import { scrollToElementBySelector } from "../../utils/smooth-scroll-to";
import { primaryCTAType, StorefrontImage } from "../../types";

const { dmSans } = FONT_FAMILIES;

interface HeroContainerProps {
  $backgroundImage: string;
  $mobileBackgroundImage?: string;
  $showLoginButton?: boolean;
  $isExpandedTimerDisplay?: boolean;
}

const HeroContainer = styled(Box)<HeroContainerProps>`
  position: relative;
  min-height: 850px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(rgba(0, 0, 0, 0.25), rgba(0, 0, 0, 0.25)),
    url(${(props) => props.$backgroundImage});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  color: #ffffff;
  @media ${({ theme }) => theme.mq.max.mobile} {
    min-height: 650px;
    background: linear-gradient(rgba(0, 0, 0, 0.25), rgba(0, 0, 0, 0.25)),
      url(${(props) => props.$mobileBackgroundImage || props.$backgroundImage});
    background-size: ${(props) =>
      props.$mobileBackgroundImage ? "contain" : "cover"};
    background-repeat: ${(props) =>
      props.$mobileBackgroundImage ? "no-repeat" : "inherit"};
    background-position: ${(props) =>
      props.$mobileBackgroundImage ? "top center" : "center"};
  }
  ${({ $isExpandedTimerDisplay }) =>
    $isExpandedTimerDisplay &&
    `
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-end;
    `}
`;

const ContentWrapper = styled(Box)`
  max-width: 900px;
  text-align: center;
`;

const MainHeadingContainer = styled(Box)`
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  margin-top: 20px;
  margin-bottom: 20px;
`;

const SubHeadingContainer = styled(Box)`
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  max-width: 50rem;
  margin: 0 auto 30px;
  @media ${({ theme }) => theme.mq.max.mobile} {
    max-width: 25rem;
  }
`;

const SignUpButton = styled(Button)`
  font-family: ${dmSans};
  background-color: #fbc44e;
  color: #000;
  border: none;
  font-size: 14px;
  border-radius: 35px;
  padding: 15px 20px;
  font-weight: 700;
  cursor: pointer;
  margin-top: 10px;
  width: 100%;
  max-width: 400px;

  &:hover {
    background-color: #f3b93c;
    color: #000;
  }

  @media ${({ theme }) => theme.mq.max.mobile} {
    padding: 12px 20px;
    max-width: 300px;
  }
`;

interface BannerHeroProps {
  backgroundImage: StorefrontImage;
  mobileBackgroundImage?: StorefrontImage;
  subheading: string;
  isExpandedTimerDisplay?: boolean;
  formId?: string;
  children?: React.ReactNode;
  countdownTimer?: string;
  heading?: string;
  primaryCta?: primaryCTAType;
  pageSectionIndex?: number;
}

const BannerHeroTimer = ({
  backgroundImage,
  mobileBackgroundImage,
  subheading,
  heading,
  formId,
  countdownTimer,
  primaryCta,
  children,
  pageSectionIndex,
}: BannerHeroProps) => {
  const location = useLocation();
  const isExpandedTimerDisplay =
    !heading && !subheading && countdownTimer ? true : false;

  const firstAdBlock = pageSectionIndex === 0;

  const handleSignupScroll = (e: React.MouseEvent) => {
    e.preventDefault();
    scrollToElementBySelector(".hs-button", {
      highlightDuration: 300,
      scale: 1.05,
      glowColor: "rgba(251, 196, 78, 0.5)",
    });
  };

  return (
    <HeroContainer
      $backgroundImage={!firstAdBlock ? backgroundImage : null}
      $mobileBackgroundImage={!firstAdBlock ? mobileBackgroundImage : null}
      padding={["10px", "20px", "100px", "100px"]}
      $isExpandedTimerDisplay={isExpandedTimerDisplay}
    >
      {children}
      <ContentWrapper>
        {subheading && (
          <MainHeadingContainer>
            <Box mr={2}>
              <Heading
                fontSize={["12px", "20px", "24px"]}
                color={COLORS.liquidGold}
                fontWeight="700"
                fontFamily={dmSans}
                textTransform="uppercase"
              >
                {subheading}
              </Heading>
            </Box>{" "}
          </MainHeadingContainer>
        )}
        {heading && (
          <SubHeadingContainer>
            <Heading
              as="h3"
              fontSize={["26px", "45px", "52px"]}
              fontFamily={dmSans}
              fontWeight="500"
              color={COLORS.white}
              lineHeight="95%"
              letterSpacing={"0%"}
            >
              {heading}
            </Heading>
          </SubHeadingContainer>
        )}
        {countdownTimer && (
          <MainHeadingContainer>
            {isExpandedTimerDisplay ? (
              <InlineCountdownTimer
                dateTime={new Date(countdownTimer).toISOString()}
                isExpandedDisplay={isExpandedTimerDisplay}
              />
            ) : (
              <InlineCountdownTimer
                dateTime={new Date(countdownTimer).toISOString()}
                title={"NEXT DEAL DROPS IN "}
                textColor={COLORS.white}
                isExpandedDisplay={isExpandedTimerDisplay}
              />
            )}
          </MainHeadingContainer>
        )}
        {formId ? (
          <HubspotForm formId={formId} />
        ) : isWhiskymofoSignupPage(location?.pathname) ? (
          <SignUpButton onClick={handleSignupScroll}>Sign Up Now!</SignUpButton>
        ) : null}
      </ContentWrapper>
    </HeroContainer>
  );
};

export { BannerHeroTimer };
